// Custom hooks for settings data fetching using React Query
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

// Hook to fetch API keys
export const useApiKeys = (options = {}) => {
  return useQuery({
    queryKey: ['apiKeys'],
    queryFn: () => apiClient.getApiKeys(),
    staleTime: 1000 * 60, // 1 minute
    ...options,
  });
};

// Hook to create a new API key
export const useCreateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => apiClient.createApiKey(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
    },
  });
};

// Hook to delete an API key
export const useDeleteApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (keyId) => apiClient.deleteApiKey(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
    },
  });
};

// Hook to fetch provisioning tokens
export const useProvisioningTokens = () => useQuery({
  queryKey: ['provisioningTokens'],
  queryFn: () => apiClient.getProvisioningTokens(),
  staleTime: 1000 * 60,
});

// Hook to create a provisioning token
export const useCreateProvisioningToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.createProvisioningToken(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provisioningTokens'] }),
  });
};

// Hook to revoke a provisioning token
export const useRevokeProvisioningToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tokenId) => apiClient.revokeProvisioningToken(tokenId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provisioningTokens'] }),
  });
};

// Hook to fetch agent-created sub-keys
export const useSubKeys = () => useQuery({
  queryKey: ['subKeys'],
  queryFn: () => apiClient.getSubKeys(),
  staleTime: 1000 * 60,
});

// Hook to revoke an agent-created sub-key
export const useRevokeSubKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (keyId) => apiClient.revokeSubKey(keyId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subKeys'] }),
  });
};

// Provider Key Vault hooks (for run_pipeline execution)
export const useProviderKeys = () => useQuery({
  queryKey: ['providerKeys'],
  queryFn: () => apiClient.getProviderKeys(),
  staleTime: 1000 * 60 * 5,
});

export const useSupportedProviders = () => useQuery({
  queryKey: ['supportedProviders'],
  queryFn: () => apiClient.getSupportedProviders(),
  staleTime: 1000 * 60 * 60, // static list, cache 1h
});

export const useSetProviderKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, apiKey }) => apiClient.setProviderKey(provider, apiKey),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['providerKeys'] }),
  });
};

export const useDeleteProviderKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider) => apiClient.deleteProviderKey(provider),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['providerKeys'] }),
  });
};
